<script setup lang="ts">
import { computed, watch } from "vue";
import { storeToRefs } from "pinia";
import { useRoute } from "vue-router";
import {
  ChartPie,
  Boxes,
  Layers,
  Truck,
  Recycle,
  FileText,
  History,
  SlidersHorizontal,
  ChevronsLeft,
  ChevronsRight,
} from "@lucide/vue";

import { useProject } from "../../stores/project/project.store";
import { useSpeckleStore } from "@/stores/speckle/speckle.store";
import { useProjectMappingStatus } from "@/composables/useProjectMappingStatus.composable";
import { useDashboard } from "@/stores/dashboard/dashboard.store";
import { useCreateBuildingProject } from "@/features/BuildingProject";
import { replayWelcomeDeck } from "@/features/WelcomeFlow";

import SidebarNavItem from "./SidebarNavItem.vue";
import SidebarNavFlyoutItem from "./SidebarNavFlyoutItem.vue";

import ReportsSelect from "@/features/ReportsSelect";

defineProps<{
  isExpanded: boolean;
}>();

const emit = defineEmits<{
  toggle: [];
}>();

const route = useRoute();
const projectStore = useProject();
const newProject = useCreateBuildingProject();
const speckle = useSpeckleStore();
const { project, permissions, hasPhaseA4, hasPhaseA5, models, activeMaterial } = storeToRefs(projectStore);
const { enableViewer, hasModels: viewerHasModels, loading: viewerLoading } = storeToRefs(speckle);

const hasModelElements = computed(() =>
  (models.value ?? []).some((m) => !!m?.referencedObjectId && m.referencedObjectId !== "UNUSED")
);
const isDashboardRoute = computed(() => route.name === "projectBreakdown");
// The LCA report has no nav row of its own — it's reached through the Reports flyout — so that row
// carries its active state, otherwise nothing in the sidebar shows where you are on the report.
const isLcaReportRoute = computed(() => route.name === "projectLcaPdfReportPage");
const isDashboardActive = computed(() => isDashboardRoute.value && !enableViewer.value);
const isModelViewerActive = computed(() => isDashboardRoute.value && enableViewer.value);
// The viewer guide anchors to the toolbar, which sits under the loading overlay until a model is on
// screen (Recenter and Reset view do not even exist before then), so its ⓘ waits for the model.
const viewerGuideDisabledReason = computed(() =>
  isModelViewerActive.value && (viewerLoading.value || !viewerHasModels.value) ? "The model is still loading" : ""
);

// On the wizard's completed screen the navigation is handed this column before any project route
// carries an :id param — the created project's id from the create store keeps every link real.
const projectId = computed(() => String(route.params.id ?? "") || newProject.getBuildingProjectId() || "");

// True only once the project STORE holds the project for the CURRENT route. During a switch the
// store still holds the previous project (hasPhaseA4/A5 lag ~3s behind the refetch in
// ProjectDetailLayout.loadProject), while projectId/hrefs are already route-fresh — gating
// Transport/Waste on this prevents BOTH switch glitches: the old project's items lingering under
// the new project's URLs (clickable into a phase the new project doesn't include), and the new
// project's items flashing in late. Hide until the store catches up, then render with the correct
// phase + lock state. Also covers clean first paint (project.value starts null → hidden → shows
// once resolved). Dashboard + Materials are always valid, so they stay ungated.
const projectLoadedForRoute = computed(() => !!project.value && project.value.id === projectId.value);

// Keep the active tab honest as soon as the project resolves for the current route — don't wait for the
// dashboard view to mount and correct it after loading. A project with no model elements can't be in
// viewer mode, so drop back to the dashboard immediately. `models` is set together with `project` in the
// store's loadProject, so gating on projectLoadedForRoute means hasModelElements is accurate here and we
// never kick a model-project out of viewer mode before its models have loaded.
watch(
  [projectLoadedForRoute, hasModelElements],
  ([loaded, projectHasModelElements]) => {
    if (loaded && !projectHasModelElements && enableViewer.value) {
      speckle.enableViewer = false;
    }
  },
  { immediate: true }
);

// Transport / Waste / Reports require mapped materials, so they're dead-ends on a brand-new
// project. Lock them until something is mapped — the nav points the user to Materials first
// instead of offering empty rooms. We start LOCKED so an empty project is dimmed on first paint
// (no "available → dimmed" flip): `isMapped` defaults false and is seeded from a per-project cache
// + the cheap dashboard summary, so reloads render the right state instantly. OR-ing the store's
// live getIsSomethingMapped() lifts the lock the moment the first material is mapped, and is the
// authoritative fallback if the summary call fails.
const dashboard = useDashboard();
const { isMapped } = useProjectMappingStatus(projectId);
const lockUntilMapped = computed(() => !(isMapped.value || dashboard.getIsSomethingMapped()));

// The only permission this column still asks about: the General row leads to an editor's page.
// `project.manageusers` and `project.delete` were read here for the Manage flyout's rows, which are
// sections of the settings page now and gate themselves there.
const canEditProject = computed(() => permissions.value?.includes("project.edit") ?? false);

/**
 * Settings: one route, `/project/:id/settings`.
 *
 * The page is a child of `ProjectDetailLayout` holding every step this project's flow contains, so
 * the project's navigation, header and revision banner stay put on arrival. It used to link at
 * `/project/:id/edit?step=…`, a sibling top-level route that replaced this whole column with the
 * wizard's own rail — a list that deleted itself the moment it was used. Gated on `project.edit`,
 * because the page is an editor's.
 *
 * This was a disclosure listing all seven steps as `#<STEP>` anchors. It is one row now: the page's
 * own right-hand rail lists those sections, marks which are complete and jumps to them, and it
 * builds that list from `getConditionalWizardFlow()` — the authority the nav's copy could only
 * mirror. Two lists of the same seven things, one of them derived and one hand-maintained, is one
 * too many, and the derived one is beside the sections it names.
 *
 * It was called "Config" until 2026-08-26, which read as a synonym of the Settings flyout that used
 * to sit at the floor of this column while that flyout held no settings at all. The page took the
 * word, the flyout became "Manage", and then the flyout's rows became sections of the page and it
 * went altogether — leaving the page itself standing in the flyout's old spot at the floor. Two
 * features were already sending users to "Project Settings" (the transport empty state and a
 * StandardValues note), so the rename made existing copy correct rather than inventing a name.
 * `/config` still resolves — see the redirect in router.ts.
 *
 * The row says "Settings", not "Project settings": this whole column IS the project's, which is why
 * its siblings are "Overview" and "Mapping" rather than "Project overview" and "Project mapping".
 * The mobile menu's row carries the scope in its tooltip for the same reason this one does — in a
 * collapsed rail or a menu that also lists account-level destinations, "Settings" alone does not say
 * whose.
 */

// Every page's guide replays from its own nav row's ⓘ, on demand, any number of times — replayWelcomeDeck
// closes whatever flow guide might already be running and logs the replay.
const openDashboardGuide = () => replayWelcomeDeck("project");

const openViewerGuide = () => replayWelcomeDeck("viewer");

// Never locked, like the viewer's — opens whichever deck matches the inventory's current state.
const openMaterialsGuide = () => replayWelcomeDeck(activeMaterial.value.length > 0 ? "materialsMap" : "materialsCreate");
</script>

<template>
  <!-- Collapsed, the root gap is gap-1 rather than gap-1.5 so the icon rail runs on one uniform 4px
       rhythm, the way the agent rail does. The group headings are hidden at this width, so the wider
       gap between groups only showed up as icons sitting lower than their agent-sidebar counterparts.
       The header keeps an 8px gap to the first item (pb-1 + gap-1), matching agent's pb-1 + gap-1 —
       sized so the first icon lands exactly on the main rail's 36px row lattice beside this one
       (row 32px + gap 4px), one slot below the toggle (RTLCA-5186). -->
  <div
    class="pb-px-8 flex h-full flex-col"
    :class="isExpanded ? 'gap-4' : 'gap-1 pt-2'">
    <!-- Expanded, the header row is the same h-px-46 + inset-bottom-shadow row that
         ProjectDetailLayout's filter bar is, and it starts at the top of the sidebar (no pt on the
         wrapper), so its hairline lands on exactly the same baseline as the filter bar's and the two
         read as one line across the layout (RTLCA-5387). The shadow — rather than a border or a
         separate hairline element — is what keeps the row a true 46px, and it matches the group
         dividers below and the sidebar's own right-hand edge. Collapsed there is no hairline to
         align, so the row keeps its pt-2 + h-fit + pb-2 offset and the icon rail runs on the same
         4px rhythm as the agent rail (AgentNavigation's header stays in step with this one). -->
    <div
      class="flex shrink-0 items-center px-2"
      :class="
        isExpanded ? 'h-px-46 justify-between shadow-[inset_0_-1px_0_0_var(--color-slate-200)]' : 'h-fit justify-center pb-1'
      ">
      <h2
        v-show="isExpanded"
        class="text-app-blue-900 t-sh3 px-2.5">
        Project
      </h2>
      <button
        type="button"
        :aria-expanded="isExpanded"
        :aria-label="isExpanded ? 'Collapse project navigation' : 'Expand project navigation'"
        class="focus-ring interactive-active rounded-8 text-type-muted hover:text-type-default flex h-7 w-7 items-center justify-center hover:bg-slate-200"
        @click="emit('toggle')">
        <component
          :is="isExpanded ? ChevronsLeft : ChevronsRight"
          class="icon-sm"
          :stroke-width="1.5" />
      </button>
    </div>
    <!-- Group dividers stay hairlines on the group itself rather than separate bordered elements.
         Expanded: an inset bottom shadow, pb-4 keeping each line where its old separator sat.
         Collapsed the lines stay so the icon rail keeps the same groups (RTLCA-5186), but they
         must not knock the icons off the 36px row lattice (row 32px + gap 4px) the main rail
         beside this one runs on — so each divider consumes exactly one icon slot (pb-9 + the
         root's gap-1 = 40px between rows) with the line an absolute zero-height element centered
         in it (bottom-4). The agent sidebar has no group dividers, so these are the one place the
         two still differ. -->
    <section
      class="relative flex flex-col"
      :class="
        isExpanded
          ? 'pb-4 shadow-[inset_0_-1px_0_0_var(--color-slate-200)]'
          : 'pb-9 after:absolute after:inset-x-0 after:bottom-4 after:h-px after:bg-slate-200 after:content-[\'\']'
      ">
      <h3
        v-show="isExpanded"
        class="t-caption-bold text-slate-450 pb-px-6 pl-px-18 pr-4 font-medium tracking-wide uppercase">
        Overview
      </h3>
      <div class="flex flex-col gap-1 px-2">
        <SidebarNavItem
          label="Dashboard"
          :to="`/project/${projectId}`"
          :icon-component="ChartPie"
          :active="isDashboardActive"
          link-id="sub-sidebar-dashboard-nav-link"
          padding-class="px-2.5 py-1.5"
          guide
          guide-always-visible
          :is-expanded="isExpanded"
          @open-guide="openDashboardGuide" />
        <SidebarNavItem
          v-if="hasModelElements"
          label="3D Model"
          :to="`/project/${projectId}?view=3d`"
          :icon-component="Boxes"
          :active="isModelViewerActive"
          link-id="sub-sidebar-model-elements-nav-link"
          padding-class="px-2.5 py-1.5"
          guide
          guide-always-visible
          :guide-disabled-reason="viewerGuideDisabledReason"
          :is-expanded="isExpanded"
          @open-guide="openViewerGuide" />
      </div>
    </section>

    <section
      class="relative flex flex-col"
      :class="
        isExpanded
          ? 'pb-4 shadow-[inset_0_-1px_0_0_var(--color-slate-200)]'
          : 'pb-9 after:absolute after:inset-x-0 after:bottom-4 after:h-px after:bg-slate-200 after:content-[\'\']'
      ">
      <h3
        v-show="isExpanded"
        class="t-caption-bold text-slate-450 pb-px-6 pl-px-18 pr-4 font-medium tracking-wide uppercase">
        Mapping
      </h3>
      <div class="flex flex-col gap-1 px-2">
        <SidebarNavItem
          label="Materials"
          :to="`/project/${projectId}/mapping`"
          :icon-component="Layers"
          :active-names="['materialMappingView']"
          link-id="sub-sidebar-material-mapping-nav-link"
          padding-class="px-2.5 py-1.5"
          guide
          guide-always-visible
          :is-expanded="isExpanded"
          @open-guide="openMaterialsGuide" />
        <!-- No <Transition> here: ProjectNavigation stays mounted across project-to-project
             navigation, and hasPhaseA4/A5 briefly flip false→true while the new project's store
             loads. A leave transition started in that window gets orphaned when the component
             re-renders mid-flight, leaving stale nav items (with the previous project's href)
             stuck in the DOM — they accumulate on every project switch. Patching the element in
             place (as Materials above does) is correct and duplicate-free. -->
        <SidebarNavItem
          v-if="projectLoadedForRoute && hasPhaseA4"
          label="Transport"
          :to="`/project/${projectId}/transport-mapping`"
          :icon-component="Truck"
          :active-names="['transportMapping']"
          link-id="sub-sidebar-transport-mapping-nav-link"
          padding-class="px-2.5 py-1.5"
          :disabled="lockUntilMapped"
          disabled-reason="Map materials first"
          :is-expanded="isExpanded" />
        <SidebarNavItem
          v-if="projectLoadedForRoute && hasPhaseA5"
          label="Waste"
          :to="`/project/${projectId}/waste`"
          :icon-component="Recycle"
          :active-names="['wasteSorting']"
          link-id="sub-sidebar-waste-sorting-nav-link"
          padding-class="px-2.5 py-1.5"
          :disabled="lockUntilMapped"
          disabled-reason="Map materials first"
          :is-expanded="isExpanded" />
      </div>
    </section>

    <!-- History and Reports, under no heading. They are readings TAKEN of a project — the record of
         what changed, and what you export out of it — so they belong to neither Overview nor
         Mapping, and a heading over two rows of different kinds was a label looking for a group.
         They briefly shared a "Settings" heading with the settings row, which now sits at the floor
         instead (below), leaving these two where they were before that grouping. -->
    <div class="flex flex-col gap-1 px-2">
      <!-- Version history mockup. Read-only view, so no permission gate and no
           mapping lock — the trail is meaningful from the moment the project is created. -->
      <SidebarNavItem
        label="History"
        :to="`/project/${projectId}/history`"
        :icon-component="History"
        :active-names="['projectVersionHistory']"
        link-id="sub-sidebar-version-history-nav-link"
        data-cy="version-history"
        padding-class="px-2.5 py-1.5"
        :is-expanded="isExpanded" />

      <SidebarNavFlyoutItem
        label="Reports"
        :icon-component="FileText"
        link-id="sub-sidebar-reports-nav-link"
        padding-class="px-2.5 py-1.5"
        :disabled="lockUntilMapped"
        disabled-reason="Map materials first"
        :active="isLcaReportRoute"
        :is-expanded="isExpanded">
        <template #flyout>
          <ReportsSelect :building-project-id="projectId" />
        </template>
      </SidebarNavFlyoutItem>
    </div>

    <!-- Settings, pinned to the sidebar floor — the position, and the chrome, of the "Manage" flyout
         that stood here until its rows became sections of the settings page. A gear-ish row at the
         bottom of a nav column is where an app keeps its settings, and this page is now the only
         thing that was behind that flyout, so it takes the spot rather than sitting in the list
         above.

         Top (not bottom) inset shadow, because `mt-auto` pushes this section down: the rule belongs
         above it, which is also where the flyout's own separator sat.

         Labelled "Settings", not "General". "General" was right while it sat under a SETTINGS
         heading that carried the subject; down here there is no heading, and a row saying "General"
         beside "History" and "Reports" would name nothing. `tooltip-text` still adds the scope for
         the collapsed rail, where only the icon shows.

         Inside the project: `projectSettings` is a PROJECT_SUB_SIDEBAR route (navigationLayout.ts),
         so this column survives being used, and the page's own rail takes over from here — it lists
         the sections, says which are complete, and jumps to them.

         Not gated on `lockUntilMapped` like Transport / Waste / Reports: those are dead ends before
         anything is mapped, while these are the assumptions you would set FIRST. Not disabled while
         previewing a frozen revision either — the layout renders the preview banner and the page
         disables Save itself, so blocking the door would hide screens the user may read. -->
    <section
      v-if="canEditProject"
      class="mt-auto flex flex-col shadow-[inset_0_1px_0_0_var(--color-slate-200)]">
      <div class="pt-px-8 flex flex-col gap-1 px-2">
        <SidebarNavItem
          label="Settings"
          tooltip-text="Project settings"
          :to="`/project/${projectId}/settings`"
          :icon-component="SlidersHorizontal"
          :active-names="['projectSettings', 'projectSettingsSpeckle']"
          link-id="sub-sidebar-settings-nav-link"
          data-cy="config"
          padding-class="px-2.5 py-1.5"
          :is-expanded="isExpanded" />
      </div>
    </section>
  </div>
</template>
